import {connect} from 'react-redux';
import {ExpandMore} from '@material-ui/icons';
import {FormattedMessage} from 'react-intl';
import React, {useState, useMemo, useEffect, createContext, useCallback} from 'react';
import {
  Accordion,
  AccordionProps,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';

import {RootState} from '../../../../store';
import {DynamicForm} from '../../../DynamicForm';
import {Answer} from '../../../../types/prompts/Answer';
import {getProjectData} from '../../../../store/project';
import {Project} from '../../../../types/project/Project';
import {Prompts} from '../../../../types/prompts/Prompts';
import {useDeploy, setDeployConfig} from '../../useDeploy';
import {DeploymentEnvironmentParametersResponse} from '../../../../api/deployment/DeploymentEnvironmentParameters';
import {DeploymentDetailsInfo} from '../../../../types/deployment/DeploymentDetailsInfo';
import {TestDeploymentQueryResponse} from '../../../../types/deployment/TestDeploymentQueryResponse';
import {Application} from '../../../../types/project/Application';

const AccordionContext = createContext<{setHasRows?: (hasRows: boolean) => void}>({});

type EnvironmentParametersProps = Omit<AccordionProps, 'children'> & {
  className?: string;
  isTaskInProgress: boolean;
  testSelectedDeployment: DeploymentDetailsInfo | null;
  historyItems: TestDeploymentQueryResponse | undefined;
  defaultEnvironmentParameters: DeploymentEnvironmentParametersResponse;
  fetchedEnvironmentParameters: DeploymentEnvironmentParametersResponse | undefined;
  selectedApplication: Application | undefined;
};

function EnvironmentParametersBase({
  className = '',
  isTaskInProgress,
  testSelectedDeployment,
  historyItems,
  defaultEnvironmentParameters,
  fetchedEnvironmentParameters,
  selectedApplication,
}: EnvironmentParametersProps) {
  const [hasRows, setHasRows] = useState(false);

  const [deployState, dispatch] = useDeploy();
  const {deployConfig} = useMemo(() => deployState, [deployState]);

  const providerValue = useMemo(() => ({hasRows, setHasRows}), [hasRows, setHasRows]);

  const historyItemSelectedEnvironment = useMemo(() => {
    const historyItemSelectedEnvironment = historyItems?.entries.find(
      deployment => deployment.id === testSelectedDeployment?.id
    )?.environment;

    if (historyItemSelectedEnvironment) {
      dispatch(
        setDeployConfig({
          environment: historyItemSelectedEnvironment,
        })
      );
    }

    return historyItemSelectedEnvironment;
  }, [dispatch, historyItems, testSelectedDeployment?.id]);

  const environmentParameters: Record<string, string> = useMemo(
    () => ({
      ...defaultEnvironmentParameters,
      ...fetchedEnvironmentParameters,
      ...(historyItemSelectedEnvironment
        ? historyItemSelectedEnvironment
        : deployConfig.environment),
    }),
    [
      historyItemSelectedEnvironment,
      deployConfig.environment,
      defaultEnvironmentParameters,
      fetchedEnvironmentParameters,
    ]
  );

  useEffect(() => {
    const hasDefaultParameters =
      defaultEnvironmentParameters && Object.keys(defaultEnvironmentParameters).length;

    if (
      hasDefaultParameters &&
      (!deployConfig.environment || !Object.keys(deployConfig.environment).length)
    ) {
      dispatch(
        setDeployConfig({
          environment: defaultEnvironmentParameters,
        })
      );
    }
  }, [defaultEnvironmentParameters, deployConfig.environment, dispatch]);

  const {prompts, defaults} = useMemo(() => {
    const prompts: Partial<Prompts>[] = [];
    const defaults: Record<string, Partial<Answer>> = {};
    const variables = {
      ...defaultEnvironmentParameters,
      ...environmentParameters,
    };

    Object.keys(variables).forEach(key => {
      prompts.push({
        key,
        text: key,
        type: 'TEXT',
        mandatory: false,
        disabled: isTaskInProgress,
        placeholder: defaultEnvironmentParameters[key],
      });

      defaults[key] = {
        key,
        value: variables[key],
      };
    });

    return {prompts, defaults};
  }, [isTaskInProgress, environmentParameters, defaultEnvironmentParameters]);

  const onBlur = useCallback(
    (formValues: Project['environment']) => {
      if (JSON.stringify(formValues) !== JSON.stringify(deployConfig.environment)) {
        dispatch(
          setDeployConfig({
            environment: formValues,
          })
        );
      }
    },
    [deployConfig.environment, dispatch]
  );

  const shouldRender = useMemo(() => prompts.length, [prompts]);

  return !shouldRender ? null : (
    <AccordionContext.Provider value={providerValue}>
      <Accordion elevation={0} defaultExpanded className={`deploy-settings ${className}`}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="deploy-settings__title-container"
        >
          <FormattedMessage tagName="h4" id="environmentParameters.title" />
        </AccordionSummary>

        <AccordionDetails className="deploy-settings__body_container">
          <DynamicForm
            onBlur={onBlur}
            defaults={defaults}
            prompts={prompts as Prompts[]}
            key={`form-${selectedApplication?.id}`}
            className="deploy-settings__form"
          />
        </AccordionDetails>
      </Accordion>
    </AccordionContext.Provider>
  );
}

const mapStateToProps = (state: RootState) => ({
  openProjectData: getProjectData(state),
});

export const EnvironmentParameters = connect(mapStateToProps)(EnvironmentParametersBase);
